import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import { useIntl } from 'react-intl'
import Logo from 'components/App/Header/Logo'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import styled from 'styled-components'
import { rem } from 'polished'
import Caption from 'LuxKit/Typography/Caption'
import NavBarUserAvatar from 'components/NavKit/NavBar/NavBarUserAvatar'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppSelector } from 'hooks/reduxHooks'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { selectItemsInCart } from 'machines/cart/selectors'
import { isDisplayableMultiCartItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import { useCallback } from 'react'
import { getWeddingGiftRegistryCartPath } from 'weddings/constants/giftRegistry'

const ItemsCounter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
  min-width: ${rem(20)};
  min-height: ${rem(20)};
  padding: 0 ${rem(3.5)};
`

function NavBarWeddingGiftRegistryBar() {
  const intl = useIntl()
  const account = useAppSelector(state => state.auth.account)
  const [loyaltyTier] = useCurrentLoyaltyTier()
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const { wedding, giftRegistryCartId, clearContext } = useWeddingGiftRegistryContext()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const setupUrl = wedding ? `/weddings/${wedding.slug}/edit` : undefined
  let cartUrl: string | undefined
  if (wedding && giftRegistryCartId) {
    cartUrl = getWeddingGiftRegistryCartPath(wedding.slug)
  }
  const numberOfActiveItems = useSelector(multiCartActorRef, (snapshot) => {
    return selectItemsInCart(snapshot, giftRegistryCartId)
      .filter(isDisplayableMultiCartItem)
      .filter(item => item.state === 'active' && item.status === 'available')
      .length
  })
  const onBackToSetupClick = useCallback(() => {
    clearContext()
  }, [clearContext])

  return (
    <Sheet padding="12 16">
      <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
        <TextButton kind="primary" variant="ghost" startIcon={<LineArrowLeftIcon />} size="medium" to={setupUrl} onClick={onBackToSetupClick}>
          {intl.formatMessage({
            id: 'KOBySkWpcHNNcBh2IKNC',
            defaultMessage: 'Back to setup',
            description: 'Button text to navigate back to wedding gift setup page',
          })}
        </TextButton>
        <Logo mobileVariant="icon" />
        <Group direction="horizontal" gap={24}>
          <TextButton kind="primary" shape="round" startIcon={<LineShoppingCartIcon />} endIcon={<ItemsCounter><Caption variant="small">{numberOfActiveItems}</Caption></ItemsCounter>} to={cartUrl} disabled={!cartUrl}>
            {intl.formatMessage({
              id: 'sPhbJGmyM0x2YTKJl7h-',
              defaultMessage: 'Gift registry cart',
              description: 'Button text to view the wedding gift registry cart',
            })}
          </TextButton>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <NavBarUserAvatar />
            <Group direction="vertical" gap={2}>
              <BodyText variant="medium" wrap="truncate" colour="normal-contrast">
                {account.fullName || intl.formatMessage({
                  id: 'yruRQ-7AtFBKNQa-80cD',
                  defaultMessage: 'Account',
                  description: 'Fallback display name in navigation when user given name is not available',
                })}
              </BodyText>
              {hasLoyaltyAccount && <BodyText variant="small" format="uppercase" weight="bold" wrap="truncate" colour={`tier-${loyaltyTier}-dark`}>
                {getLoyaltyTierDisplayName(loyaltyTier)}
              </BodyText>}
            </Group>
          </Group>
        </Group>
      </Group>
    </Sheet>
  )
}

export default NavBarWeddingGiftRegistryBar
